<script setup>
import { Link, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Loading from 'vue-loading-overlay';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css'

const props = defineProps({
    year: String,
    allPackages: Object,
    filters: Object,
    flash: Object,
    user: Object,
});

let packageSearch = ref('');
let searchCriteria = ref('');
const $toast = useToast();
dayjs.extend(advancedFormat);
let isLoading = ref(false);

//look for changes in the searchCriteria and packageSearch using watch with debounce
watch([packageSearch, searchCriteria], debounce(function (value) {
    router.get('/packages/searchPackages', { packageSearch: value[0], searchCriteria: value[1] }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 1000));


onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

function deletePackage(value) {
    new Swal({
        title: 'Are you sure?',
        text: "All the data related to this package will be deleted!",
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonText: 'I am sure!'
    }).then((result) => {
        if (result.isConfirmed) {
            setTimeout(() => {
                window.location.href = '/packages/delete/' + value;
            }, 300);
        }
    })
}

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Bookings Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-header border-bottom">
                    <h5 class="card-title">Package Manager</h5>
                    <div class="row mx-2">
                        <div class="col-sm-12 col-md-4 col-lg-6">

                        </div>
                        <div class="col-sm-12 col-md-8 col-lg-6">
                            <div
                                class="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-md-end justify-content-center align-items-center flex-sm-nowrap flex-wrap me-1">
                                <div class="user_role me-3">
                                    <select id="search" class="form-select text-capitalize" v-model="searchCriteria"
                                        name="searchCriteria" required>
                                        <option value="" selected="selected" disabled="disabled"> Select Search By
                                        </option>
                                        <option value="PackageName" class="text-capitalize">Package Name</option>
                                        <option value="HotelName" class="text-capitalize">Hotel Name</option>
                                    </select>
                                </div>
                                <div class="w-px-200 pb-3 pb-sm-0">
                                    <div id="DataTables_Table_0_filter" class="dataTables_filter"><label>
                                            <input type="search" class="form-control" v-model="packageSearch"
                                                name="packageSearch" placeholder="Search.."
                                                aria-controls="DataTables_Table_0"></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">

                            </div>
                            <div class="dt-action-buttons text-end pt-3 pt-md-0"
                                v-if="$page.props.user.permissions.includes('add-packages')">
                                <div class="dt-buttons">
                                    <Link type="button" class="btn btn-primary" href="/packages/create/"
                                        preserve-scroll>
                                    <i class='bx bx-plus bx-tada'></i> Add New Package
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <table v-if="allPackages"
                            class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Package</th>
                                    <th>Hotel Name</th>
                                    <th>Booking Date From</th>
                                    <th>Travel Date From</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="packages in allPackages.data" :key="packages.id">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="avatar-wrapper">
                                                <div class="avatar me-2"><span
                                                        class="avatar-initial rounded-circle bg-label-warning">{{
                                                            getInitials(packages.package_name)
                                                        }}</span>
                                                </div>
                                            </div>
                                            <div class=" d-flex flex-column">
                                                <span class="emp_name text-truncate">{{ packages.package_name }}</span>
                                                <small class="emp_post text-truncate text-muted">
                                                    {{ packages.meal_type }}
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ packages.hotel.name }}</td>
                                    <td>{{ dayjs(packages.booking_date_from).format('Do MMMM YYYY') }}</td>
                                    <td>{{ dayjs(packages.travel_date_from).format('Do MMMM YYYY') }}</td>
                                    <td>
                                        <span v-if="packages.status == '1'" class="badge bg-label-success">Active</span>
                                        <span v-else class="badge bg-label-danger">Inactive</span>
                                    </td>
                                    <td>
                                        <div class="d-inline-block text-nowrap">
                                            <Link class="btn btn-sm btn-icon text-warning"
                                                v-if="$page.props.user.permissions.includes('view-packages')"
                                                :href="'/packages/show/' + packages.id"
                                                data-bs-original-title="View Package"><i class="bx bx-search-alt"></i>
                                            </Link>
                                            <Link :href="'/packages/copy/' + packages.id"
                                                v-if="$page.props.user.permissions.includes('copy-packages')"
                                                class="btn btn-sm btn-icon text-primary" alt="Copy Package"><i
                                                class="bx bx-copy-alt"></i>
                                            </Link>
                                            <button class="btn btn-sm btn-icon dropdown-toggle hide-arrow"
                                                data-bs-toggle="dropdown" aria-expanded="false"><i
                                                    class="bx bx-dots-vertical-rounded"></i>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-end m-0" style="">
                                                <Link :href="'/packages/edit/' + packages.id"
                                                    v-if="$page.props.user.permissions.includes('edit-packages')"
                                                    class="dropdown-item text-info">
                                                Edit</Link>

                                                <Link @click="deletePackage(packages.id)" href="#"
                                                    v-if="$page.props.user.permissions.includes('remove-packages')"
                                                    class="dropdown-item text-danger">Delete
                                                </Link>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row" v-if="allPackages">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status"
                                    aria-live="polite">Showing {{ allPackages.from }} to {{ allPackages.to }} of {{
                                        allPackages.total
                                    }} Packages</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="allPackages.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>